import { ArrowRight, CalendarClock } from "lucide-react";
import { ASSETS, CALENDLY_URL } from "@/lib/site";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function Hero() {
  return (
    <section id="top" className="relative bg-white pt-28 md:pt-32 pb-16 md:pb-24 overflow-hidden">
      {/* gold corner accent */}
      <div className="absolute top-24 right-0 w-[40%] h-[2px] bg-gold/40" />

      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        {/* Left text */}
        <div className="lg:col-span-7">
          <div className="rise rise-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[hsl(var(--line))] bg-mist">
            <span className="w-1.5 h-1.5 rounded-full bg-gold" />
            <span className="eyebrow text-navy/70">For Schools, Districts &amp; Communities</span>
          </div>

          <h1 className="rise rise-2 font-serif font-bold text-navy mt-6 text-[44px] sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
            Solving the crisis in
            <span className="block italic font-medium text-gold">attendance, behavior</span>
            and staff retention.
          </h1>

          <p className="rise rise-3 mt-6 text-base sm:text-lg text-navy/70 max-w-xl leading-relaxed">
            CDaught Speaks partners with K-12 schools, colleges, and community organizations to
            cultivate environments conducive to growth — through relationship building, leadership
            development, and social-emotional learning that actually moves the needle.
          </p>

          <div className="rise rise-4 mt-9 flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => scrollTo("contact")}
              data-testid="hero-book-btn"
              className="btn-gold inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-sm text-[14px] font-semibold"
            >
              Book CDaught <ArrowRight size={16} />
            </button>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-discovery-btn"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-sm text-[14px] font-semibold border border-navy/80 text-navy hover:bg-navy hover:text-white transition-colors"
            >
              <CalendarClock size={16} /> Schedule a Discovery Call
            </a>
          </div>
        </div>

        {/* Right image */}
        <div className="lg:col-span-5 relative rise rise-3">
          <div className="relative">
            <div className="absolute -top-6 -left-6 w-32 h-32 border-2 border-gold hidden md:block" />
            <img
              src={ASSETS.action1}
              alt="CDaught speaking on stage"
              data-testid="hero-image"
              loading="eager"
              className="relative w-full h-[480px] md:h-[600px] object-cover shadow-2xl"
            />
            <div className="absolute -bottom-6 -right-6 bg-navy text-white p-5 max-w-[220px] hidden md:block">
              <div className="text-gold eyebrow">Now Booking</div>
              <div className="font-serif text-xl mt-1 leading-tight">2025–2026 School Year</div>
            </div>
          </div>
        </div>
      </div>

      {/* tagline strip */}
      <div className="mt-16 md:mt-24 border-y border-[hsl(var(--line))] bg-mist overflow-hidden">
        <div className="marquee-track flex gap-12 whitespace-nowrap py-4">
          {Array.from({ length: 2 }).flatMap((_, k) =>
            [
              "Cultivating Environments Conducive to Growth",
              "Leadership Starts With Me",
              "Bridge the Gap",
              "From Boys to Gentlemen",
              "See the Seed",
              "Lead How You Live",
            ].map((t, i) => (
              <span key={`${k}-${i}`} className="font-serif text-navy/55 text-2xl">
                {t} <span className="text-gold">✦</span>
              </span>
            ))
          )}
        </div>
      </div>
    </section>
  );
}
